// This file contains date validation logic for disabling calendar
//   dates when a user with fiscalStartDate and fiscalCutoffDate preferences
//   is setting usage on material assets.

// Logic for UDOT date preferences as example:
// "fiscalStartDate" : "0701",
// "fiscalCutoffDate" : "0720",

// If today is between July 1 and July 20 including July 20
// - Allow usage dates from July 1 of the previous FY through today.

// If today is between July 21 and June 30 including June 30
// - Allow usage dates from July 1 of the current FY through today.

import { useMemo } from 'react';
import {
  add,
  getDaysInMonth,
  getYear,
  isAfter,
  isEqual,
  isWithinInterval,
} from 'date-fns';
import * as R from 'ramda';

import { usePreferences } from '@atom/hooks/usePreferences';
import { getMonthName } from '@atom/utilities/timeUtilities';

interface Data {
  invalidUsageDate: (date: Date) => boolean;
  INVALID_USAGE_DATE_MSG: string;
}

const year = getYear(new Date());

const getMonthDayString = (monthDay: string): string => {
  // monthDay preferences are four digit mmdd strings
  const monthDayIsLegit = monthDay?.length === 4;
  return monthDayIsLegit
    ? [R.slice(0, 2, monthDay), R.slice(2, 4, monthDay)].join('-')
    : '';
};

export const useMaterialUsageValidation = (): Data => {
  const preferences = usePreferences();
  const { fiscalStartDate, fiscalCutoffDate } = preferences;

  const fiscalStartMonthDay = getMonthDayString(fiscalStartDate);
  const fiscalCutoffMonthDay = getMonthDayString(fiscalCutoffDate);
  const fullFiscalStartDate = `${year}-${fiscalStartMonthDay}`;
  const fullFiscalCutoffDate = `${year}-${fiscalCutoffMonthDay}`;

  // memoizing error msg string building
  // - same message for each date based on fiscal preferences
  const INVALID_USAGE_DATE_MSG = useMemo(() => {
    if (!fiscalStartDate || !fiscalCutoffDate) {
      return '';
    }
    const fiscalStartMonth = fiscalStartMonthDay.split('-')[0];
    let fiscalStartDay = fiscalStartMonthDay.split('-')[1];
    if (fiscalStartDay.startsWith('0')) {
      fiscalStartDay = fiscalStartDay.substring(1);
    }
    const fiscalStartMonthIndex = Number(fiscalStartMonth) - 1;
    const fiscalStartMonthName = getMonthName(fiscalStartMonthIndex);
    const fiscalEndMonthName = getMonthName((fiscalStartMonthIndex - 1) % 12);
    const lastDayEndMonth = getDaysInMonth(
      new Date(year, (fiscalStartMonthIndex - 1) % 12),
    );

    const fiscalCutoffMonth = fiscalCutoffMonthDay.split('-')[0];
    let fiscalCutoffDay = fiscalCutoffMonthDay.split('-')[1];
    if (fiscalCutoffDay.startsWith('0')) {
      fiscalCutoffDay = fiscalCutoffDay.substring(1);
    }
    const fiscalCutoffMonthIndex = Number(fiscalCutoffMonth) - 1;
    const fiscalCutoffMonthName = getMonthName(fiscalCutoffMonthIndex);

    const cutoffDateName = !!fiscalCutoffDate
      ? `${fiscalCutoffMonthName} ${fiscalCutoffDay}`
      : '';
    return [
      `Usage dates must be within the current fiscal year (${fiscalStartMonthName} ${fiscalStartDay}-${fiscalEndMonthName} ${lastDayEndMonth}).`,
      !!fiscalCutoffDate
        ? `After it ends, you can only enter usage from that year until ${cutoffDateName}.`
        : '',
    ].join(' ');
  }, [fiscalStartDate, fiscalCutoffDate]);

  const isPastFiscalYearStart =
    isAfter(new Date(), fullFiscalStartDate) ||
    isEqual(new Date(), fullFiscalStartDate);
  const fiscalYear = isPastFiscalYearStart ? year : year - 1;

  const isWithinGracePeriod = isWithinInterval(new Date(), {
    start: fullFiscalStartDate,
    end: add(new Date(fullFiscalCutoffDate), { days: 1 }),
  });

  const validationYear = isWithinGracePeriod ? fiscalYear - 1 : fiscalYear;

  const isValidMaterialUsage = (date: Date) =>
    isWithinInterval(date, {
      start: new Date(`${validationYear}-${fiscalStartMonthDay}`),
      end: new Date(),
    });

  const invalidUsageDate = (date: Date): boolean => {
    return !fiscalStartDate || !fiscalCutoffDate
      ? false
      : !isValidMaterialUsage(date);
  };

  return {
    invalidUsageDate,
    INVALID_USAGE_DATE_MSG,
  };
};
